import { StrictMode, useCallback, useEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'

import './styles.css'
import { initTheme } from './theme'
import { AppShell } from './components/AppShell'
import { ApiError, getSession, logout, type Session } from './api'
import { CannotReach, CouldNotSignOut, SignIn } from './pages/SignIn'
import { NotOpen } from './pages/NotOpen'
import { Dashboard } from './pages/Dashboard'
import { Inbox } from './pages/Inbox'
import { NewEnvelope } from './pages/NewEnvelope'
import { EnvelopeDetail } from './pages/EnvelopeDetail'
import { EnvelopeDetailPreview } from './pages/EnvelopeDetailPreview'
import { WorkflowBuilderExploration } from './pages/WorkflowBuilderExploration'
import { OnboardingPreview } from './pages/OnboardingPreview'
import { Sign } from './pages/Sign'
import { Verify } from './pages/Verify'
import { Templates } from './pages/Templates'
import { Organization } from './pages/admin/Organization'
import { Members } from './pages/admin/Members'
import { Recipient, Recipients } from './pages/admin/Recipients'
import { OrgSignIn } from './pages/admin/SignIn'
import { Billing } from './pages/admin/Billing'
import { JoinAccounts } from './pages/JoinAccounts'
import { AcceptTerms } from './pages/AcceptTerms'
import { Account, AccountFarewell, DeleteAccount, type Farewell } from './pages/Account'
import { Signup } from './pages/Signup'
import { Connect } from './pages/Connect'
import { captureSignupIntentFromLocation, signupIntentQueryPresent } from './signupIntent'
import { forgetReauth, pendingReauth, rememberReauth, settleSession, type Reauth } from './reauth'
import { signOutThen } from './signOut'

initTheme()

if (typeof window !== 'undefined' && signupIntentQueryPresent(window.location.search)) {
  captureSignupIntentFromLocation(window.location.search)
  const clean = new URL(window.location.href)
  ;['utm_source', 'plan', 'seats', 'completions', 'active', 'retention', 'collaboration', 'legal_hold'].forEach(
    (k) => clean.searchParams.delete(k),
  )
  window.history.replaceState({}, '', clean.pathname + clean.search + clean.hash)
}

function Shell() {
  // null while we are still asking the server, so the sign-in form does not
  // flash in front of a sender who is already authenticated. 'closed' is the
  // server's own answer that the deployment is not open to the public yet:
  // distinct from unreachable, and its own state rather than a flag beside a
  // made-up session, so an answer of either kind replaces it outright. An
  // operator signing in through the holding page passes the door, and refresh
  // then finds an ordinary session. `failed` is a question that got no
  // session back at all, which is neither of those and is shown as itself.
  // `stuck` is a sign-in as somebody else that the deletion page's "Sign in
  // again" refused and could not sign out (reauth.ts settleSession).
  const [session, setSession] = useState<Session | 'closed' | { failed: unknown } | { stuck: string } | null>(null)
  // Set when the session ended on purpose, by deleting the account or signing
  // out everywhere, so the page can say that it happened. The session it ended
  // is dropped at the same moment, so nothing behind the message goes on
  // asking the server as somebody who is no longer signed in.
  const [farewell, setFarewell] = useState<Farewell | null>(null)
  // The address the deletion page asked to sign in again as, kept across the
  // sign-out so the sign-in page can be fixed to it, and across a Google
  // round trip in sessionStorage (reauth.ts).
  // The ref is what refresh reads, so that it need not change when this does.
  const [again, setAgainState] = useState<Reauth | null>(() => pendingReauth())
  const againRef = useRef(again)
  const setAgain = useCallback((r: Reauth | null) => {
    againRef.current = r
    setAgainState(r)
  }, [])
  // Set when a sign-in again came back as somebody else and was signed out.
  const [againRefused, setAgainRefused] = useState('')
  // Why the last Sign out, or Sign in again, was refused; the shell shows it.
  const [signOutError, setSignOutError] = useState('')
  const navigate = useNavigate()
  const location = useLocation()
  // refresh navigates through this, so that it is not a new function each
  // time the location changes and does not ask the server again on every
  // navigation.
  const navigateRef = useRef(navigate)
  useEffect(() => {
    navigateRef.current = navigate
  }, [navigate])

  const refresh = useCallback(() => {
    // The deletion page asked this person to sign in again. Somebody else
    // signing in instead is signed out at once and never shown their own
    // deletion page, as the app does (review-2-docs request 3), and is not
    // shown it either when the sign-out fails (review-3 web-3).
    settleSession(getSession, logout, againRef.current)
      .then((r) => {
        if (r.kind === 'stuck') {
          setAgain(r.again)
          setAgainRefused(r.refused)
          setSession({ stuck: r.refused })
          return
        }
        // A sign-in after the request ran out is an ordinary one: it does not
        // open the deletion page it was asked from (review-3 web-2).
        if (r.leaveDeletion && window.location.pathname.startsWith('/account/delete')) {
          navigateRef.current('/', { replace: true })
        }
        if (r.again !== undefined) setAgain(r.again)
        if (r.refused !== undefined) setAgainRefused(r.refused)
        setSession(r.session)
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.code === 'not_open') {
          setSession('closed')
          return
        }
        // Not a sign-in page. Nothing is known about which ways in the server
        // offers, and every one of them, the operator form included, talks to
        // the server that just failed to answer.
        setSession({ failed: e })
      })
  }, [setAgain])

  useEffect(refresh, [refresh])

  const leave = (kind: Farewell) => {
    setSession(null)
    setFarewell(kind)
    // Nothing is left to sign in again to: the sign-in page that follows is
    // an ordinary one, whoever uses it next.
    forgetReauth()
    setAgain(null)
    setAgainRefused('')
  }

  if (farewell) {
    return (
      <AccountFarewell
        kind={farewell}
        onContinue={() => {
          setFarewell(null)
          navigate('/', { replace: true })
          refresh()
        }}
      />
    )
  }
  if (session === null) {
    return <main className="page page-narrow"><p className="muted">Loading…</p></main>
  }
  if (session === 'closed') {
    return <NotOpen onSignedIn={refresh} />
  }
  if ('failed' in session) {
    return (
      <CannotReach
        error={session.failed}
        onRetry={() => {
          setSession(null)
          refresh()
        }}
      />
    )
  }
  if ('stuck' in session) {
    return (
      <CouldNotSignOut
        refused={session.stuck}
        onRetry={() => {
          setSession(null)
          refresh()
        }}
      />
    )
  }
  if (!session.authenticated) {
    return (
      <SignIn
        offers={{ ...session.sign_in, terms_url: session.terms_url }}
        again={again?.address}
        againRefused={againRefused}
        onLeaveAgain={() => {
          forgetReauth()
          setAgain(null)
          setAgainRefused('')
          // Off the deletion page, so the ordinary sign-in that follows, by
          // anybody, does not open their own deletion page (review-3 web-2).
          navigate('/', { replace: true })
        }}
        onSignedIn={() => {
          setAgain(null)
          refresh()
        }}
      />
    )
  }
  if (session.terms_required) {
    // Only the page the server names. A fallback here once sent people asked
    // to read the terms to the pricing page.
    return (
      <AcceptTerms
        termsUrl={session.terms_url}
        onAccepted={refresh}
        onSignOut={() => logout().then(refresh)}
      />
    )
  }
  if (session.needs_signup) {
    return <Signup session={session} onCreated={refresh} onSignOut={() => logout().then(refresh)} />
  }
  if (location.pathname === '/connect') {
    return <Connect session={session} />
  }

  return (
    <AppShell
      session={session}
      signOutError={signOutError}
      onSignOut={() => {
        setSignOutError('')
        void signOutThen(logout, refresh, setSignOutError)
      }}
    >
      <Routes>
        <Route path="/" element={<Dashboard />} />
        {/* What was sent TO this person, as opposed to what their
            organization sent. Two different questions, two different lists. */}
        <Route path="/inbox" element={<Inbox />} />
        <Route path="/new" element={<NewEnvelope session={session} />} />
        <Route path="/envelopes/:id" element={<EnvelopeDetail sends={session.memberships.length > 0} />} />
        <Route path="/templates" element={<Templates session={session} />} />
        <Route path="/verify" element={<Verify />} />
        {/* The organization the page acts on is in the path, not in state: a
            person may administer several, and two tabs disagreeing about which
            one they are looking at is not acceptable on pages whose actions
            cannot be undone. */}
        <Route path="/orgs/:orgId/settings" element={<Organization session={session} />} />
        <Route path="/orgs/:orgId/members" element={<Members session={session} />} />
        <Route path="/orgs/:orgId/members/invitations" element={<Members session={session} />} />
        <Route path="/orgs/:orgId/invitations" element={<OrgPathRedirect to="members/invitations" />} />
        <Route path="/orgs/:orgId/recipients/:contactId" element={<Recipient session={session} />} />
        <Route path="/orgs/:orgId/recipients" element={<Recipients session={session} />} />
        <Route path="/orgs/:orgId/contacts" element={<OrgPathRedirect to="recipients" />} />
        <Route path="/orgs/:orgId/sign-in" element={<OrgSignIn session={session} />} />
        <Route path="/orgs/:orgId/billing" element={<Billing session={session} />} />
        {/* Where a sign-in callback sends somebody who has just proved a second
            credential in a browser already signed in as somebody else. Nothing
            is joined until they answer here. */}
        <Route path="/accounts/join" element={<JoinAccounts onJoined={refresh} />} />
        {/* The person's own account: who they are signed in as, signing out
            everywhere, and deleting it. "Sign in again" signs out first, so
            the sign-in page that follows opens a new session rather than
            sitting a second one beside the old, and it returns here. */}
        <Route
          path="/account"
          element={<Account session={session} onSignedOutEverywhere={() => leave('signed_out_everywhere')} />}
        />
        <Route
          path="/account/delete"
          element={
            <DeleteAccount
              session={session}
              onSignInAgain={(address) => {
                const at = Date.now()
                rememberReauth(address, session.identity.party_id, at)
                setAgain({ address, party_id: session.identity.party_id, at })
                setAgainRefused('')
                setSignOutError('')
                // Refused, the person is still signed in as themselves: the
                // sign-in it asked for is not coming, so it is forgotten.
                void signOutThen(logout, refresh, (sentence) => {
                  forgetReauth()
                  setAgain(null)
                  setSignOutError(sentence)
                })
              }}
              onDeleted={() => leave('deleted')}
            />
          }
        />
        {/* A signed-in person can be standing on a path the shell has no view
            for: /signin, where the Google callback sends its failures and
            where the operator form then signs them in, or an old link. Send
            them to the dashboard rather than render an empty page under a
            working top bar. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  )
}

/** Old admin paths, kept so a link already sent still lands on the page. */
function OrgPathRedirect({ to }: { to: string }) {
  const { orgId = '' } = useParams()
  return <Navigate to={`/orgs/${orgId}/${to}`} replace />
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <Routes>
        {/* The signing view is deliberately outside the app shell: a signer is
            not a user of our product, they are someone who received a link. */}
        <Route path="/sign/:token" element={<Sign />} />
        {/* Photographed by shots and walked by edges. Not linked from the
            product; the first-run it previews is not built yet. */}
        <Route path="/preview/onboarding" element={<OnboardingPreview />} />
        {import.meta.env.DEV && (
          <>
            <Route path="/preview/envelope-detail" element={<EnvelopeDetailPreview />} />
            <Route path="/preview/workflow" element={<WorkflowBuilderExploration />} />
          </>
        )}
        <Route path="*" element={<Shell />} />
      </Routes>
    </BrowserRouter>
  </StrictMode>,
)
